import { yupResolver } from '@hookform/resolvers/yup';
import { CreateElectronicSignatureDto } from '@zorro/clients';
import { getFullName } from '@zorro/common-utils';
import { formatDateISO, getNow } from '@zorro/shared/formatters';
import {
  useFlowId,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

const normalizeName = (name: string) =>
  name.trim().replace(/\s+/gu, ' ').toLowerCase();

export const getManualElectionConsentSchema = (employeeFullName: string) =>
  yup.object().shape({
    signedName: yup
      .string()
      .required('Full legal name is a required field')
      .test(
        'matches-employee-full-name',
        "Must match the employee's full legal name",
        (signedName = '') =>
          normalizeName(signedName) === normalizeName(employeeFullName)
      ),
  });

export type ManualElectionConsentFields = yup.InferType<
  ReturnType<typeof getManualElectionConsentSchema>
>;

export enum SignButtonState {
  INVALID = 'INVALID',
  VALID = 'VALID',
  SUBMITTED = 'SUBMITTED',
}

export const useManualElectionConsent = (
  onboardingPeriodId: string,
  employeeId: string
) => {
  const flowId = useFlowId();

  // Recorded once per step mount, as a mutation: a query would be refetched by other
  // mutations' invalidation, minting orphan snapshot rows and silently swapping the id
  // (and possibly the content) under a signature the operator already gave.
  const { tryMutate: tryCreateReviewSnapshot, data: reviewSnapshot } =
    useMonolithMutation({
      method: 'onboardingPeriodsControllerCreateManualElectionReviewSnapshot',
      shouldReloadPage: false,
      shouldInvalidateQueries: false,
      shouldShowLoadingOverlay: false,
    });

  const requestedPeriodIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (requestedPeriodIdRef.current === onboardingPeriodId) {
      return;
    }
    requestedPeriodIdRef.current = onboardingPeriodId;
    void tryCreateReviewSnapshot([onboardingPeriodId], { flowId });
  }, [onboardingPeriodId, flowId, tryCreateReviewSnapshot]);

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const consentSchema = getManualElectionConsentSchema(
    employee ? getFullName(employee) : ''
  );

  const form = useForm<ManualElectionConsentFields>({
    mode: 'all',
    resolver: yupResolver(consentSchema),
    defaultValues: {
      signedName: '',
    },
  });

  const [signStatus, setSignStatus] = useState(SignButtonState.VALID);

  const getElectronicSignature = async (): Promise<
    CreateElectronicSignatureDto | undefined
  > => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!userTimeZone || !reviewSnapshot) {
      return undefined;
    }

    // The employee name can change after signing; react-hook-form only re-checks on a field event
    const isSignedNameValid = await form.trigger('signedName');
    if (!isSignedNameValid) {
      setSignStatus(SignButtonState.VALID);
      return undefined;
    }

    const { signedName } = form.getValues();
    return {
      userTimeZone,
      presentedDate: formatDateISO(getNow()),
      userAgent: navigator.userAgent,
      signedName,
      reviewSnapshotId: reviewSnapshot.id,
    };
  };

  return {
    form,
    signStatus,
    setSignStatus,
    isSigned: signStatus === SignButtonState.SUBMITTED,
    reviewSnapshot,
    getElectronicSignature,
  };
};
